/**
 * What counts as a description worth sending.
 *
 * Shared by the page and the endpoint on purpose. The endpoint has to check
 * because anything can POST to it; the page has to check so it can say
 * something useful instead of appearing to ignore the button. Two copies of
 * this would drift, and the way you find out is a visitor typing something the
 * page accepts and the server rejects.
 */

/** Long enough to describe a sprite, short enough that nobody smuggles a novel in. */
export const MAX_WANT = 280;

export type WantCheck = { ok: true; want: string } | { ok: false; why: string };

/**
 * Which way the thing is facing.
 *
 * Left unsaid, a model picks a viewpoint per sprite and you get a bed seen from
 * above next to a fridge seen head on. That reads as a mistake even when both
 * sprites are good, because game art is only coherent if everything in it is
 * drawn from the same place. So the view is a choice the caller makes, not
 * something to be inferred from the noun.
 */
export const VIEWS = ["front", "three-quarter", "top-down"] as const;
export type View = (typeof VIEWS)[number];

export const VIEW_BRIEF: Record<View, string> = {
	front:
		"Straight on, at eye level, like an item icon or a side-scroller prop. No top surface is visible. " +
		"This is the clearest view for anything defined by its face: a door, a fridge, a mug, a sign.",
	"three-quarter":
		"From slightly above and slightly to the right, so you can see the front face and a shallow slice of the top " +
		"at the same time. This is the view most colony and farming games use. Keep the top surface shallow, " +
		"around a third of the height of the front, and keep the angle identical for every object.",
	"top-down":
		"Directly from above, looking down. You see the top surface and nothing of the front. " +
		"This is the classic role-playing-game view, and it suits furniture, rugs, tables and anything you walk around.",
};

export const VIEW_LABEL: Record<View, string> = {
	front: "From the front",
	"three-quarter": "Three quarter",
	"top-down": "From above",
};

/**
 * Whether the sprite should move.
 *
 * Left to the model this varies, because "would this move in a real game" is a
 * judgement rather than a rule, and the same description can reasonably go
 * either way. Someone filling a set needs to be able to settle it.
 *
 * `still` is enforced rather than merely asked for: a spec that comes back
 * animated is flattened to one frame. `animate` can only be asked for, since
 * nothing can make a crate interesting.
 */
export const MOTIONS = ["auto", "animate", "still"] as const;
export type Motion = (typeof MOTIONS)[number];

export const MOTION_BRIEF: Record<Motion, string> = {
	auto: "Animate it only if the subject genuinely moves. Most objects do not.",
	animate:
		"Animate this one. Find the part of the subject that moves, flickers, fills, spins or glows, " +
		"give it a frames block, and drive only those parts from it. If the subject has no moving part, " +
		"animate a highlight or a glint rather than wobbling the whole object.",
	still: "Do not animate this. No frames block, one frame only.",
};

export const MOTION_LABEL: Record<Motion, string> = {
	auto: "Auto",
	animate: "Animate",
	still: "Still",
};

export function checkMotion(raw: unknown): Motion {
	return (MOTIONS as readonly string[]).includes(raw as string) ? (raw as Motion) : "auto";
}

export function checkView(raw: unknown): View {
	return (VIEWS as readonly string[]).includes(raw as string) ? (raw as View) : "front";
}

/**
 * The canvases somebody may ask for by name.
 *
 * A closed list rather than two free numbers, for the same reason the shapes
 * are a closed list. These are the sizes games actually use; an open pair is a
 * way to ask for a 512 by 512 sprite made of four hundred parts; and a list is
 * a control somebody uses in one click rather than a form they fill in.
 *
 * Naming a size is a paid feature, but that is decided where the account is
 * known. This only decides what is a size at all.
 */
export const SIZES: Record<string, [number, number]> = {
	"16x16": [16, 16],
	"24x24": [24, 24],
	"32x32": [32, 32],
	"48x48": [48, 48],
	"64x64": [64, 64],
	"32x48": [32, 48],
	"48x32": [48, 32],
};

/**
 * A named size, or null for "whatever suits the subject", which is the default
 * and usually the better sprite. Anything unrecognised becomes null rather than
 * an error: an old page sending a size this build dropped should still draw.
 */
export function checkSize(raw: unknown): [number, number] | null {
	return typeof raw === "string" && Object.hasOwn(SIZES, raw) ? SIZES[raw] : null;
}

/**
 * Subjects this will not draw.
 *
 * Deliberately short and deliberately narrow. This is a tool for game art, so
 * weapons, blood, monsters and gore are all legitimate subjects and none of
 * them are here: a filter that refuses "a rusty sword" is a broken tool, not a
 * safe one. What is here is sexual anatomy and acts, and slurs.
 *
 * It is a cost saving rather than the safety measure. The real filter is the
 * instruction in the system prompt, which understands intent and spelling and
 * catches what a word list cannot. This exists so the obvious attempt is turned
 * away in a millisecond for nothing, instead of spending thirty seconds and a
 * model call to arrive at the same answer.
 *
 * Matched on word boundaries, because the alternative is refusing to draw a
 * cockpit, a shuttlecock, or Scunthorpe.
 */
const REFUSED = [
	"anal",
	"anus",
	"bdsm",
	"blowjob",
	"boob",
	"boobs",
	"clit",
	"cum",
	"cunt",
	"dick",
	"dildo",
	"ejaculate",
	"erection",
	"fellatio",
	"genital",
	"genitalia",
	"handjob",
	"hentai",
	"labia",
	"masturbate",
	"masturbating",
	"nude",
	"nudes",
	"orgasm",
	"penis",
	"porn",
	"porno",
	"pornography",
	"pussy",
	"scrotum",
	"semen",
	"sex",
	"testicle",
	"testicles",
	"tits",
	"titties",
	"vagina",
	"vulva",
	"wank",
];

/*
 * `(?:e?s)?` rather than `s?`, because a word ending in s takes "es".
 *
 * With `s?` the singular was caught and "penises" walked straight through,
 * which is the kind of hole a list like this is worth nothing without.
 */
const REFUSED_RE = new RegExp(String.raw`\b(${REFUSED.join("|")})(?:e?s)?\b`, "i");

/**
 * What somebody is told when a subject is turned away.
 *
 * One sentence, no lecture, and it names what the tool is FOR rather than what
 * the person is. Somebody typing this to see what happens has found out; there
 * is nothing to be gained by moralising at them, and a paragraph of policy in a
 * pixel art tool reads as ridiculous.
 */
export const REFUSAL = "That is not something this draws. Try a thing you would find in a game: an object, a creature, a machine.";

/** True when a description names a subject the tool will not draw. */
export const refuses = (want: string): boolean => REFUSED_RE.test(want);

export function checkWant(raw: unknown): WantCheck {
	if (typeof raw !== "string") return { ok: false, why: "Say what you would like drawn." };
	const want = raw.trim();
	if (!want) return { ok: false, why: "Type what you would like drawn, then press Draw it." };
	if (want.length > MAX_WANT) return { ok: false, why: `Keep it under ${MAX_WANT} characters.` };
	if (refuses(want)) return { ok: false, why: REFUSAL };
	return { ok: true, want };
}
